// Seller Intelligence entry point — until we get proper Sidebar entries,
// this hub gives one-click access to every Karthik-owned SI surface.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SIHub.module.css';

const CARDS = [
  {
    title: 'Catalogue Health',
    subtitle: 'Suppression reasons · missing attributes · image quality · fix engine',
    path: '/dashboard/seller-intelligence/catalogue-health',
    accent: 'blue',
    bullets: [
      'Per-SKU IDQ score with confidence',
      'ASIN drawer with revenue-at-risk per gap',
      'Suggested value + one-click fix apply / undo',
      'MAC / RAC / image chips',
    ],
  },
  {
    title: 'Inventory Health Monitor',
    subtitle: 'Stock-out risk · days of cover · turnover corridor · dead stock',
    path: '/dashboard/seller-intelligence/inventory-health',
    accent: 'green',
    bullets: [
      'Overview → 6 sub-feature tabs',
      'Health states: Healthy / Low / Warning / Critical / Out',
      'Turnover corridor with per-SKU sub-states',
      'Dead-stock classifier (Stranded / Unproven / Clearing / Dead)',
    ],
  },
  {
    title: 'Inventory Performance Metrics',
    subtitle: 'Excess · stranded · aging · IPI · storage costs',
    path: '/dashboard/seller-intelligence/inventory-performance',
    accent: 'amber',
    bullets: [
      'Cover > lead + review + safety = Excess',
      "Amazon's stranded report verbatim, sorted by days-to-removal",
      'IPI account measures with Amazon thresholds',
      'Storage bill + LTS surcharge next-month attribution',
    ],
  },
  {
    title: 'Restock Recommendations',
    subtitle: 'Ranked list · order quantities · order-by dates · walk-forward',
    path: '/dashboard/seller-intelligence/restock',
    accent: 'purple',
    bullets: [
      'Quantity = demand × horizon − on-hand − inbound + safety',
      'Order-by via day-by-day walk-forward projection',
      'Priority = urgency + stakes (revenue-at-risk)',
      'MOQ / pack-size per-SKU overrides',
    ],
  },
  {
    title: 'Restock Alerts',
    subtitle: 'Predictive Restocking Engine · deadlines from every inventory feature',
    path: '/dashboard/seller-intelligence/restock-alerts',
    accent: 'red',
    bullets: [
      'Notification matrix as config — 23 of 34 alerts live',
      'Order-by, stranded, surcharge cutoffs, dead stock, seasonal peaks',
      'Grouped by issue and by product, tap-through to the owning page',
      'Detection only — sending comes with the alerting system',
    ],
  },
];

const SIHub = () => {
  const navigate = useNavigate();
  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.title}>Seller Intelligence</h1>
        <p className={styles.subtitle}>
          Every surface below reads the same shared data —
          suppressions, feature vectors, stock-out states — so numbers
          never disagree across pages.
        </p>
      </div>

      <div className={styles.grid}>
        {CARDS.map((c) => (
          <button key={c.path}
                  className={`${styles.card} ${styles[`accent_${c.accent}`]}`}
                  onClick={() => navigate(c.path)}>
            <div className={styles.cardTitle}>{c.title}</div>
            <div className={styles.cardSubtitle}>{c.subtitle}</div>
            <ul className={styles.cardBullets}>
              {c.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <div className={styles.cardCta}>Open →</div>
          </button>
        ))}
      </div>

      <div className={styles.footer}>
        <div className={styles.footTitle}>Data currently loaded</div>
        <div className={styles.footBody}>
          Signed-in seller's Amazon data. Endeavar (seller_id{' '}
          <code>A2Y6MZUVZMJ4R9</code>, owner <code>karthik@ecombuddha.in</code>)
          has 1,239 SKUs and ~1.45M report rows synced — richest live account.
        </div>
        <div className={styles.footTitle} style={{ marginTop: 16 }}>
          Refresh vs Fetch
        </div>
        <div className={styles.footBody}>
          <b>Refresh</b> (↻ button on each page) re-reads what's already in the
          DB — instant, no rate limits.<br/>
          <b>Fetch from Amazon</b> (Catalogue Health page only, 3/day cap)
          triggers a fresh SP-API pull. Inventory pages read the reports landed
          by the Amazon connect-time sync — there's no nightly refresh yet.
        </div>
      </div>
    </div>
  );
};

export default SIHub;
